import React from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

import Card from 'components/App/Card';
import Loading from 'components/App/Loading';
import SelectValidation from 'components/App/SelectValidation';
import StayChart from 'components/Charts/StayChart';
import StayTable from 'components/Table/StayTable';
import RevenueChart from 'components/Charts/RevenueChart';
import RevenueTable from 'components/Table/RevenueTable';
import PriceTable from 'components/Table/PriceTable';

import { CURRENCY_LABEL } from 'constants/index';
import { currencyFormat, formatNumber } from 'utils/currencyFormat';
import AnalyticsPropTypes from 'propTypeModels/analytics';
import { ANALYTIC_TYPE, DATA_TIMES } from 'constants/analytics';

import styles from './AnalyticsPerformance.module.scss';

const COMPARE_OPTIONS = [
  { label: 'Cùng phân khúc', value: 'SAME_SEGMENT' },
  { label: 'Thị trường', value: 'MARKET' },
  { label: 'Năm trước', value: 'LAST_YEAR' },
];

function AnalyticsPerformance({
  performanceCharts,
  performanceStatics,
  performanceOverviews,
  performanceChartsLoading,
  performanceStaticsLoading,
  performanceOverviewsLoading,

  getPerformanceCharts,
  getPerformanceStatics,
  getPerformanceOverviews,
}) {
  const { search } = useLocation();

  const [dataTime, setDataTime] = React.useState(DATA_TIMES[0].value);
  const [compareWith, setCompareWith] = React.useState(COMPARE_OPTIONS[0].value);

  const searchQuery = React.useMemo(() => {
    const params = new URLSearchParams(search);

    return {
      place: params.get('place'),
      type: params.get('type') || ANALYTIC_TYPE.EFFICIENCY,
    };
  }, [search]);

  const compareLabel = React.useMemo(() => {
    if (compareWith === 'LAST_YEAR') return 'năm ngoái';
    const option = COMPARE_OPTIONS.find((item) => item.value === compareWith);
    return option ? option.label.toLowerCase() : 'năm ngoái';
  }, [compareWith]);

  const getCompareText = React.useCallback((current, compare) => {
    const curVal = current ?? 0;
    const compVal = compare ?? 0;
    if (compVal === 0) {
      if (curVal > 0) return `+100% so với ${compareLabel}`;
      return `0% so với ${compareLabel}`;
    }
    const percent = ((curVal - compVal) / compVal) * 100;
    const rounded = Math.round(percent * 10) / 10;
    const sign = rounded >= 0 ? '+' : '';
    return `${sign}${formatNumber(rounded)}% so với ${compareLabel}`;
  }, [compareLabel]);

  const onChangeSelection = React.useCallback(({ value }) => {
    setDataTime(value);
  }, []);

  const onChangeCompare = React.useCallback(({ value }) => {
    setCompareWith(value);
  }, []);

  React.useEffect(() => {
    getPerformanceStatics({ place: searchQuery.place });
  }, [getPerformanceStatics, getPerformanceCharts, searchQuery.place]);

  React.useEffect(() => {
    const payload = {
      days: dataTime,
      place: searchQuery.place,
      compareWith,
    };

    getPerformanceOverviews(payload);

    getPerformanceCharts(payload);
  }, [
    dataTime,
    getPerformanceCharts,
    getPerformanceOverviews,
    searchQuery.place,
    compareWith,
  ]);

  return (
    <div className={styles.Page}>
      <Loading
        loaderOnPage
        loading={(
          performanceStaticsLoading
          || performanceOverviewsLoading
          || performanceChartsLoading
        )}
      />

      <Card className={styles.ScoreImpactCard}>
        <div className={styles.Title}>
          <h2 className={styles.h2}>Các yếu tố tác động đến điểm của bạn</h2>

          <p className={styles.description}>
            Những thống kê NÀY cho biết hiệu suất hoạt động của bạn trên
            Bookese.com trong 1 tháng qua. Mức độ hiện diện cực kỳ quan trọng
            đối với tỷ lệ chuyển đổi chung của Bạn. Số liệu sẽ được cập nhật
            vào ngày 5 mỗi tháng.
          </p>
        </div>

        <Card className={styles.OverallData}>
          <div className={styles.Data}>
            <p>Điểm thứ hạng</p>
            <p>
              {formatNumber(performanceStatics?.rankingScore)}
            </p>
          </div>

          <div className={styles.Data}>
            <p>Tỷ lệ chuyển đổi</p>
            <p>
              {formatNumber(performanceStatics?.conversionRate || 0)}
              %
            </p>
          </div>

          <div className={styles.Data}>
            <p>Giá bình quân</p>
            <p>
              {currencyFormat({
                currency: CURRENCY_LABEL,
                value: performanceStatics?.averagePrice ?? 0,
              })}
            </p>
          </div>

          <div className={styles.Data}>
            <p>Điểm đánh giá</p>
            <p>
              {`${formatNumber(performanceStatics?.averageOverviewRating ?? 0)}/ 10`}
            </p>
          </div>
        </Card>
      </Card>

      <Card className={styles.RevenueOverall}>
        <div className={styles.FlexWrap}>
          <div className={styles.ItemWrap}>
            <p>Cho xem dữ liệu trong:</p>

            <SelectValidation
              value={dataTime}
              options={DATA_TIMES}
              selectedClassName={styles.Select}
              onChange={onChangeSelection}
            />
          </div>

          <div className={styles.ItemWrap}>
            <p>So sánh với:</p>

            <SelectValidation
              value={compareWith}
              options={COMPARE_OPTIONS}
              selectedClassName={styles.Select}
              onChange={onChangeCompare}
            />
          </div>
        </div>

        <Card className={styles.OverallData}>
          <div className="">
            <p>
              Đêm phòng
            </p>
            <p>{formatNumber(performanceOverviews?.data?.totalNights ?? 0)}</p>
            <p>
              {getCompareText(
                performanceOverviews?.data?.totalNights,
                performanceOverviews?.compareData?.totalNights,
              )}
            </p>
          </div>

          <span className={styles.Separator} />

          <div className="">
            <p>Doanh thu</p>
            <p>
              {currencyFormat({
                currency: CURRENCY_LABEL,
                value: performanceOverviews?.data?.totalAmountReceived ?? 0,
              })}
            </p>
            <p>
              {getCompareText(
                performanceOverviews?.data?.totalAmountReceived,
                performanceOverviews?.compareData?.totalAmountReceived,
              )}
            </p>
          </div>

          <span className={styles.Separator} />

          <div className="">
            <p>Giá bình quân</p>
            <p>
              {currencyFormat({
                currency: CURRENCY_LABEL,
                value: performanceOverviews?.data?.averagePrice ?? 0,
              })}
            </p>
            <p>
              {getCompareText(
                performanceOverviews?.data?.averagePrice,
                performanceOverviews?.compareData?.averagePrice,
              )}
            </p>
          </div>
        </Card>
      </Card>

      <Card className={styles.ChartCard}>
        <div className={styles.FlexWrapTitle}>
          <h2 className={styles.Title}>
            Đêm phòng
          </h2>
          <div className={styles.legend}>
            <div className={styles.item}>
              <div className={`${styles.background} ${styles.night}`} />
              <p className={styles.description}>Đêm phòng</p>
            </div>

            <div className={styles.item}>
              <div className={`${styles.background} ${styles.lastYear}`} />
              <p className={styles.description}>{`Đêm phòng ${compareLabel}`}</p>
            </div>
          </div>
        </div>

        <div className={styles.ChartWrapper}>
          <div className={styles.ChartScroll}>
            <StayChart dataTime={dataTime} data={performanceCharts} compareLabel={compareLabel} />
          </div>
        </div>

        {!performanceChartsLoading && (
          <StayTable
            dataTime={dataTime}
            data={performanceCharts}
            compareWith={compareWith}
          />
        )}
      </Card>

      <Card className={styles.ChartCard}>
        <div className={styles.FlexWrapTitle}>
          <h2 className={styles.Title}>
            Doanh thu
          </h2>
          <div className={styles.legend}>
            <div className={styles.item}>
              <div className={`${styles.background} ${styles.night}`} />
              <p className={styles.description}>Doanh thu</p>
            </div>

            <div className={styles.item}>
              <div className={`${styles.background} ${styles.lastYear}`} />
              <p className={styles.description}>{`Doanh thu ${compareLabel}`}</p>
            </div>
          </div>
        </div>

        <div className={styles.ChartWrapper}>
          <div className={styles.ChartScroll}>
            <RevenueChart
              dataTime={dataTime}
              data={performanceCharts}
              chartFor="Doanh thu"
              dataKey="actualAmountReceived"
              compareLabel={compareLabel}
            />
          </div>
        </div>

        {!performanceChartsLoading && (
          <RevenueTable
            dataTime={dataTime}
            data={performanceCharts}
            compareWith={compareWith}
          />
        )}
      </Card>

      <Card className={styles.ChartCard}>
        <div className={styles.FlexWrapTitle}>
          <h2 className={styles.Title}>
            Giá bình quân
          </h2>
          <div className={styles.legend}>
            <div className={styles.item}>
              <div className={`${styles.background} ${styles.night}`} />
              <p className={styles.description}>Giá bình quân</p>
            </div>

            <div className={styles.item}>
              <div className={`${styles.background} ${styles.lastYear}`} />
              <p className={styles.description}>{`Giá bình quân ${compareLabel}`}</p>
            </div>
          </div>
        </div>

        <div className={styles.ChartWrapper}>
          <div className={styles.ChartScroll}>
            <RevenueChart
              dataTime={dataTime}
              dataKey="averagePrice"
              data={performanceCharts}
              chartFor="Giá bình quân"
              compareLabel={compareLabel}
            />
          </div>
        </div>

        {!performanceChartsLoading && (
          <PriceTable
            dataTime={dataTime}
            data={performanceCharts}
            compareWith={compareWith}
          />
        )}
      </Card>
    </div>
  );
}

AnalyticsPerformance.propTypes = {
  performanceChartsLoading: PropTypes.bool.isRequired,
  performanceStaticsLoading: PropTypes.bool.isRequired,
  performanceOverviewsLoading: PropTypes.bool.isRequired,
  performanceStatics: AnalyticsPropTypes.performanceStatics,
  performanceOverviews: AnalyticsPropTypes.performanceOverviews,
  performanceCharts: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]).isRequired,

  getPerformanceCharts: PropTypes.func.isRequired,
  getPerformanceStatics: PropTypes.func.isRequired,
  getPerformanceOverviews: PropTypes.func.isRequired,
};

AnalyticsPerformance.defaultProps = {
  performanceStatics: null,
  performanceOverviews: null,
};

export default AnalyticsPerformance;
